// Op semantics shared by the server room, the client's confirmed state and
// the local store, so every replica applies ops identically (spec §6.1:
// per-property last-writer-wins in server order).
import type { BoardObject, Card, ChecklistItem, Op } from './types.ts';
import { applyItemOp, ITEM_KEYS } from './cards.ts';

/**
 * Applies update props. A prop set to null (or undefined) removes the key:
 * JSON can't carry undefined, so on the wire "unset" is null.
 */
export function applyUpdate(obj: BoardObject, props: Partial<BoardObject> | Record<string, unknown>): BoardObject {
  const next: Record<string, unknown> = { ...obj };
  for (const [k, v] of Object.entries(props)) {
    if (k === 'id' || k === 'type') continue;
    if (v === null || v === undefined) delete next[k];
    else next[k] = v;
  }
  return next as unknown as BoardObject;
}

/** An op in wire form: update/item props that are undefined become null. */
export function toWire(op: Op): Op {
  if (op.t === 'update') return { t: 'update', id: op.id, props: nullify(op.props) as Partial<BoardObject> };
  if (op.t === 'item' && op.props) return { t: 'item', id: op.id, itemId: op.itemId, props: nullify(op.props) as Partial<ChecklistItem> };
  return op;
}

function nullify(props: object): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) out[k] = v === undefined ? null : v;
  return out;
}

export function opObjectId(op: Op): string {
  return op.t === 'create' ? op.obj.id : op.id;
}

/**
 * Ops that turn `current` into exactly `target` (used to restore a version).
 * Deletes first, then creates and per-object updates of the props that differ.
 */
export function diffOps(current: ReadonlyMap<string, BoardObject>, target: readonly BoardObject[]): Op[] {
  const want = new Map(target.map((o) => [o.id, o]));
  const ops: Op[] = [];
  for (const id of current.keys()) {
    const t = want.get(id);
    if (!t || t.type !== current.get(id)!.type) ops.push({ t: 'delete', id });
  }
  for (const o of target) {
    const cur = current.get(o.id);
    if (!cur || cur.type !== o.type) {
      ops.push({ t: 'create', obj: o });
      continue;
    }
    const a = cur as unknown as Record<string, unknown>, b = o as unknown as Record<string, unknown>;
    const props: Record<string, unknown> = {};
    for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
      if (k === 'id' || k === 'type') continue;
      if (o.type === 'card' && k === 'items') continue; // items change one at a time (spec §13.2)
      if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) props[k] = b[k] ?? null;
    }
    if (Object.keys(props).length) ops.push({ t: 'update', id: o.id, props: props as Partial<BoardObject> });
    if (o.type === 'card') ops.push(...diffItems(cur as Card, o));
  }
  return ops;
}

function diffItems(cur: Card, target: Card): Op[] {
  const ops: Op[] = [];
  for (const itemId of Object.keys(cur.items)) {
    if (!Object.hasOwn(target.items, itemId)) ops.push({ t: 'item', id: cur.id, itemId, props: null });
  }
  for (const [itemId, want] of Object.entries(target.items)) {
    const have = Object.hasOwn(cur.items, itemId) ? cur.items[itemId] : undefined;
    if (!have) {
      ops.push({ t: 'item', id: cur.id, itemId, props: { ...want } });
      continue;
    }
    const props: Record<string, unknown> = {};
    for (const k of ITEM_KEYS) if (have[k] !== want[k]) props[k] = want[k] ?? null;
    if (Object.keys(props).length) ops.push({ t: 'item', id: cur.id, itemId, props: props as Partial<ChecklistItem> });
  }
  return ops;
}

/**
 * Applies an op to a plain object map. Returns false if it can't apply
 * (create of an existing id, update/delete of a missing one).
 */
export function applyOpToMap(map: Map<string, BoardObject>, op: Op): boolean {
  switch (op.t) {
    case 'create':
      if (map.has(op.obj.id)) return false;
      map.set(op.obj.id, op.obj);
      return true;
    case 'update': {
      const obj = map.get(op.id);
      if (!obj) return false;
      map.set(op.id, applyUpdate(obj, op.props));
      return true;
    }
    case 'delete':
      return map.delete(op.id);
    case 'item': {
      const obj = map.get(op.id);
      if (obj?.type !== 'card') return false;
      const next = applyItemOp(obj, op.itemId, op.props);
      if (next === obj) return false;
      map.set(op.id, next);
      return true;
    }
  }
}
